import imageCompression from 'browser-image-compression'

import { IMAGE_SURFACES, type ImageSurface } from './imageAssetFields'

interface ImageCompressionPreset {
  maxSizeMB: number
  maxWidthOrHeight: number
  initialQuality: number
  fileType: string
}

export const IMAGE_COMPRESSION_PRESETS: Record<ImageSurface, ImageCompressionPreset> = {
  [IMAGE_SURFACES.profiles]: {
    maxSizeMB: 0.4,
    maxWidthOrHeight: 512,
    initialQuality: 0.8,
    fileType: 'image/webp',
  },
  [IMAGE_SURFACES.personas]: {
    maxSizeMB: 0.75,
    maxWidthOrHeight: 1024,
    initialQuality: 0.82,
    fileType: 'image/webp',
  },
  [IMAGE_SURFACES.characters]: {
    maxSizeMB: 1,
    maxWidthOrHeight: 1600,
    initialQuality: 0.84,
    fileType: 'image/webp',
  },
  [IMAGE_SURFACES.worlds]: {
    maxSizeMB: 1,
    maxWidthOrHeight: 1600,
    initialQuality: 0.84,
    fileType: 'image/webp',
  },
  [IMAGE_SURFACES.openWorldSetups]: {
    maxSizeMB: 1,
    maxWidthOrHeight: 1600,
    initialQuality: 0.84,
    fileType: 'image/webp',
  },
}

const SUPPORTED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/bmp',
])

const MAX_UPLOAD_BYTES = 15 * 1024 * 1024

function extensionForMimeType(mimeType = ''): string {
  switch (mimeType) {
    case 'image/jpeg':
      return 'jpg'
    case 'image/png':
      return 'png'
    case 'image/webp':
      return 'webp'
    case 'image/bmp':
      return 'bmp'
    default:
      return 'bin'
  }
}

export function getImageCompressionPreset(surface: string): ImageCompressionPreset {
  return (IMAGE_COMPRESSION_PRESETS as Record<string, ImageCompressionPreset>)[surface]
    || IMAGE_COMPRESSION_PRESETS[IMAGE_SURFACES.characters]
}

export function validateImageFile(file: File): void {
  if (!(file instanceof File)) {
    throw new Error('Please choose an image file.')
  }

  if (!SUPPORTED_MIME_TYPES.has(file.type)) {
    throw new Error('Supported image types are JPG, PNG, WebP, and BMP.')
  }

  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error('Please choose an image smaller than 15 MB.')
  }
}

interface ImageDimensions {
  width: number | null
  height: number | null
}

export async function readImageDimensions(file: File): Promise<ImageDimensions> {
  const objectUrl = URL.createObjectURL(file)

  try {
    const dimensions = await new Promise<ImageDimensions>((resolve, reject) => {
      const image = new Image()
      image.onload = () => {
        resolve({
          width: Number(image.naturalWidth) || null,
          height: Number(image.naturalHeight) || null,
        })
      }
      image.onerror = () => reject(new Error('Unable to read the selected image.'))
      image.src = objectUrl
    })

    return dimensions
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export async function compressImageForSurface(file: File, surface: string): Promise<File> {
  validateImageFile(file)

  const preset = getImageCompressionPreset(surface)
  const baseOptions = {
    useWebWorker: true,
    maxSizeMB: preset.maxSizeMB,
    maxWidthOrHeight: preset.maxWidthOrHeight,
    initialQuality: preset.initialQuality,
  }

  try {
    return await imageCompression(file, {
      ...baseOptions,
      fileType: preset.fileType,
    })
  } catch {
    return imageCompression(file, baseOptions)
  }
}

export function getFileExtension(file: File | null | undefined): string {
  return extensionForMimeType(file?.type || '')
}
